import { useEffect, useRef } from 'react';
import { LiveIcon } from '@/components/features/BottomNav/icons/LiveIcon';
import { useSnakeMarketsFindSeriesHistory } from '@/lib/endpoints';
import type { SnakeMarketsFindSeriesHistorySubCategoryCode } from '@/lib/models';
import { cn } from '@/lib/utils';
import { formatEtClockTime, isClosed, nextWindowCloseIso } from '@/lib/snake-market';
import { useNowMs } from './useNowMs';

/** How many recent settled windows to show (backend already caps at 10). */
const PAST_CAP = 10;

/** When the live window closes, refetch this often to roll the timeline forward. */
const CLOSE_REFETCH_MS = 2500;
/** Bounded so a stalled series can't poll forever (~20s — covers settle + ingest). */
const CLOSE_REFETCH_MAX_TRIES = 8;

export interface SnakeTimelineStripProps {
  /** Active instrument (e.g. `BTC`). */
  instrument: string;
  /** Active resolution sub-category code (drives the history fetch + cadence). */
  subCategoryCode: string | undefined;
  /**
   * Close time of the window the user is actually viewing on the page — drives
   * the "you are here" bounded marker. It moves only when the page advances
   * (Go To Live / tapping the live pill), never on the clock.
   */
  viewedCloseTime?: string | null;
  /** Tapping the now-live pill jumps the page to the live instance (page only). */
  onGoLive?: () => void;
  /** Tapping a settled past pill opens its frozen "past view" (by local market id). */
  onSelectPast?: (marketId: string) => void;
}

/**
 * Horizontal, scrollable timeline of snake instances rendered below the lobby
 * chart (Figma node 1658-65424). Reads `useSnakeMarketsFindSeriesHistory` and
 * lays the pills out **chronologically** (oldest → newest): up to the most
 * recent {@link PAST_CAP} settled instances, then the live instance, then the
 * "upcoming" instance.
 *
 * The upcoming pill's time is **known in advance** — windows are back-to-back,
 * so the next close = current close + one period. We compute it client-side
 * (preferring the real `next` once the backend posts the UPCOMING row ~15s
 * before close) and show it the whole round, with no refresh/poll needed.
 *
 * Two independent markers ride the pills:
 * - **LIVE** (`LiveIcon`) marks the window that is objectively live right now.
 *   It moves forward on its own at a window boundary (clock + the bounded
 *   refetch below).
 * - **You-are-here** (purple bounded border, from `viewedCloseTime`) marks the
 *   window the user is actually on. It moves **only** when the page advances
 *   (Go To Live / tapping the live pill) — never automatically. After a close
 *   the two diverge: the user stays on the just-closed window while LIVE moves
 *   to the next.
 *
 * The bounded refetch only rolls *objective* state forward (settling the closed
 * window so its result arrow lands, surfacing the next live instance); it does
 * not move the user.
 */
export function SnakeTimelineStrip({
  instrument,
  subCategoryCode,
  viewedCloseTime,
  onGoLive,
  onSelectPast,
}: SnakeTimelineStripProps) {
  const { data, refetch } = useSnakeMarketsFindSeriesHistory(
    { instrument, subCategoryCode: subCategoryCode as SnakeMarketsFindSeriesHistorySubCategoryCode },
    { query: { enabled: Boolean(subCategoryCode) } },
  );
  const nowMs = useNowMs();

  const current = data?.current ?? null;
  // The real next window (UPCOMING) — only present ~15s before close. Preferred
  // over the derived time when available (it carries the true close).
  const next = data?.next ?? null;

  // Is the series' current instance still live, or has its window closed?
  const currentIsLive = Boolean(current) && !isClosed(current?.closeTime, nowMs);

  // Next window time: the real UPCOMING close when the backend has it, else the
  // deterministic derivation (current close + one period) so the pill shows
  // immediately, all round long — no extra fetch.
  const upcomingClose = next?.closeTime ?? nextWindowCloseIso(current?.closeTime, subCategoryCode);

  // While there's no live instance (the window closed, or a between-windows gap
  // left us with none), refetch until the backend settles the just-closed window
  // (so its result arrow lands) and promotes the next window to current. Bounded
  // so a stalled/empty series can't poll forever; re-arms on the next close.
  // This rolls OBJECTIVE state forward only — the you-are-here marker tracks
  // `viewedCloseTime`, so the user is never moved by this.
  const awaitingLive = !currentIsLive;
  useEffect(() => {
    if (!awaitingLive) return;
    let tries = 0;
    void refetch();
    const id = setInterval(() => {
      tries += 1;
      void refetch();
      if (tries >= CLOSE_REFETCH_MAX_TRIES) clearInterval(id);
    }, CLOSE_REFETCH_MS);
    return () => clearInterval(id);
  }, [awaitingLive, refetch]);

  // Keep the newest (live/upcoming) pills in view by default; the user scrolls
  // left to reveal older windows.
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [data, currentIsLive]);

  // newest-first → ascending, cap to the most recent few.
  const past = (data?.history ?? [])
    .filter((item) => Boolean(item.closeTime))
    .slice(0, PAST_CAP)
    .reverse();

  if (past.length === 0 && !current) return null;

  // "You are here" = the window the page is showing. Match pills by window close
  // (all strikes of a window share it). Until the page reports its selection,
  // fall back to the live pill so there's no marker-less flash.
  const viewedMs = parseMs(viewedCloseTime);
  const isHere = (closeIso: string | null | undefined, fallbackLive: boolean): boolean =>
    viewedMs == null ? fallbackLive : sameWindow(closeIso, viewedMs);

  const currentHere = current ? isHere(current.closeTime, currentIsLive) : false;

  return (
    // Outer scroller; inner row is `w-max` + `ml-auto` so it sits at the right
    // edge when it fits and scrolls left↔right when it overflows (a plain
    // `justify-end` on the scroller would make the overflowing left unreachable).
    <div ref={scrollRef} className="overflow-x-auto scrollbar-hide">
      <div className="ml-auto flex h-[44px] w-max items-center gap-2 pr-[14px]">
        {past.map((item) => (
          <TimelinePill
            key={item.marketId}
            closeIso={item.closeTime}
            wonSide={item.wonSide}
            muted
            isHere={isHere(item.closeTime, false)}
            onClick={onSelectPast ? () => onSelectPast(item.marketId) : undefined}
          />
        ))}

        {/* The series' current instance — live, or the just-closed window the
            user is still on while it awaits the next (it keeps the bounded
            you-are-here marker; LIVE moves to the next pill). */}
        {current && (
          <TimelinePill
            key={current.marketId}
            closeIso={current.closeTime}
            isLive={currentIsLive}
            isHere={currentHere}
            onClick={currentIsLive && !currentHere ? onGoLive : undefined}
          />
        )}

        {/* After close the next window is the one that's now LIVE — and the
            tappable "go to live" target (the user hasn't moved there yet). */}
        {!currentIsLive && upcomingClose && (
          <TimelinePill
            key="live"
            closeIso={upcomingClose}
            isLive
            isHere={isHere(upcomingClose, false)}
            onClick={isHere(upcomingClose, false) ? undefined : onGoLive}
          />
        )}

        {/* While live, a passive label of the next window's time (no live
            instance to jump to yet). */}
        {currentIsLive && upcomingClose && (
          <TimelinePill key="upcoming" closeIso={upcomingClose} isHere={isHere(upcomingClose, false)} />
        )}
      </div>
    </div>
  );
}

/**
 * One timeline pill. Two independent markers compose onto it:
 * - `isLive` shows the LIVE icon (the objectively-live window).
 * - `isHere` draws the purple bounded border (the window the user is on).
 * `wonSide` adds the settled Up/Down arrow; `muted` dims the time for
 * settled/pending past windows. `onClick` makes it the tappable go-live target.
 */
function TimelinePill({
  closeIso,
  wonSide,
  isLive = false,
  isHere = false,
  muted = false,
  onClick,
}: {
  closeIso: string | null | undefined;
  /** `'UP'`/`'DOWN'` renders the result arrow; omit for live/upcoming pills. */
  wonSide?: string | null;
  isLive?: boolean;
  isHere?: boolean;
  /** Dim the time (settled/pending) vs. the brighter upcoming label. */
  muted?: boolean;
  onClick?: () => void;
}) {
  const shape = isHere
    ? 'rounded-[49px] border-[1.5px] border-purple shadow-[0px_0px_4px_0px_var(--color-stroke-soft)]'
    : 'rounded-[40px] border border-stroke-soft';
  const className = cn('flex h-[28px] shrink-0 items-center gap-1 whitespace-nowrap bg-surface px-2.5', shape);
  const textClass = isLive || isHere ? 'text-purple font-medium' : muted ? 'text-ink-muted' : 'text-ink';
  const inner = (
    <>
      {isLive && <LiveIcon className="size-[18px] shrink-0 text-purple" />}
      {wonSide === 'UP' && <RoundArrow dir="up" />}
      {wonSide === 'DOWN' && <RoundArrow dir="down" />}
      <span className={cn('text-[16px] uppercase tracking-[-0.32px]', textClass)}>{formatEtClockTime(closeIso)}</span>
    </>
  );
  return onClick ? (
    <button type="button" onClick={onClick} className={cn(className, 'cursor-pointer')}>
      {inner}
    </button>
  ) : (
    <div className={className}>{inner}</div>
  );
}

/** Parse an ISO timestamp to unix-ms, or `null` when missing/unparseable. */
function parseMs(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/** True when an ISO close time is the same window instant as `targetMs`. */
function sameWindow(closeIso: string | null | undefined, targetMs: number): boolean {
  const ms = parseMs(closeIso);
  return ms != null && ms === targetMs;
}

/**
 * Round-result arrow (Figma node 1658-65100): a bold, gradient-filled directional
 * arrow — green (Up) / red (Down). The source vector points right; we rotate it
 * −90° for Up and +90° for Down, mirroring the Figma component.
 */
function RoundArrow({ dir }: { dir: 'up' | 'down' }) {
  const id = `snake-round-arrow-${dir}`;
  const [from, to] =
    dir === 'up'
      ? ['var(--color-success)', 'var(--color-success-dark)']
      : ['var(--color-danger)', 'var(--color-danger-dark)'];
  return (
    <svg
      viewBox="0 0 14 10.5193"
      className={`h-2.5 w-3.5 shrink-0 ${dir === 'up' ? '-rotate-90' : 'rotate-90'}`}
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M13.3239 6.72527L8.40528 10.2934C8.21285 10.44 7.97765 10.5193 7.73577 10.5193C7.49389 10.5193 7.25869 10.44 7.06627 10.2934C6.84762 10.1183 6.67275 9.89474 6.55554 9.64034C6.43834 9.38594 6.382 9.10773 6.39101 8.82778V7.02453H1.4033C1.27241 7.02349 1.14266 7.00014 1.01963 6.95547C0.69928 6.8057 0.431183 6.56334 0.249947 6.25969C0.0687116 5.95603 -0.0173406 5.60502 0.0029005 5.25197C-0.0143495 4.90341 0.072357 4.55757 0.252014 4.25837C0.43167 3.95917 0.696175 3.7201 1.01195 3.5715C1.13434 3.52405 1.26436 3.49934 1.39562 3.4986H6.38333V1.69152C6.37433 1.41156 6.43066 1.13335 6.54787 0.878957C6.66508 0.624559 6.83994 0.400963 7.05859 0.225896C7.25247 0.0793134 7.48888 0 7.73193 0C7.97499 0 8.2114 0.0793134 8.40528 0.225896L13.3278 3.79403C13.5444 3.97066 13.7175 4.19465 13.8339 4.44874C13.9503 4.70284 14.0069 4.98026 13.9992 5.25965C14.0076 5.53896 13.951 5.81641 13.8338 6.07009C13.7166 6.32378 13.542 6.54676 13.3239 6.72143V6.72527Z"
        fill={`url(#${id})`}
      />
      <defs>
        <linearGradient id={id} x1="7.8" y1="0" x2="7.8" y2="10.5193" gradientUnits="userSpaceOnUse">
          <stop style={{ stopColor: from }} />
          <stop offset="1" style={{ stopColor: to }} />
        </linearGradient>
      </defs>
    </svg>
  );
}
